import React, { useEffect, useState } from "react";
import cx from "classnames";
import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Slider from "src/components/slider";
import Button from "src/components/button";
import TruncateWithTooltip from "src/components/truncateWithTooltip";

import styles from "./common.module.scss";
import { ALWAYS_VISIBLE_UID } from "src/services/experiences/leadsAndReports/leadFields";
import { LeadFieldConfig } from "src/types/experiences/leadsAndReports";

interface EditFieldsModalProps {
  show: boolean;
  fields: LeadFieldConfig[];
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onDone: (fields: LeadFieldConfig[]) => void;
}

interface SortableOptionProps {
  field: LeadFieldConfig;
  // eslint-disable-next-line no-unused-vars
  onToggle: (fieldId: string) => void;
}

const SortableOption: React.FC<SortableOptionProps> = ({ field, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.fieldId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition,
    zIndex: isDragging ? 9999 : "auto",
    position: isDragging ? "relative" : "static",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cx(styles.editFieldsOption, { [styles.editFieldsHidden]: !field.visible })}
    >
      <span className={styles.editFieldsDragHandle} {...attributes} {...listeners}>
        <i className="paperflite-drag" />
      </span>
      <TruncateWithTooltip text={field.name || "-"} className={styles.editFieldsName} />
      <button
        className={styles.editFieldsToggle}
        onClick={() => onToggle(field.fieldId)}
        aria-label={field.visible ? "Hide field" : "Show field"}
      >
        <i
          className={cx({
            "paperflite-hidden-eye": field.visible,
            "paperflite-eye": !field.visible,
          })}
        />
        <span>{field.visible ? "Hide" : "Show"}</span>
      </button>
    </div>
  );
};

export const EditFieldsModal: React.FC<EditFieldsModalProps> = ({
  show,
  fields,
  onClose,
  onDone,
}) => {
  const [items, setItems] = useState<LeadFieldConfig[]>(fields);

  useEffect(() => {
    if (show) setItems(fields);
  }, [show, fields]);

  const sortableFields = items.filter((f) => f.uid !== ALWAYS_VISIBLE_UID);

  const toggleVisibility = (fieldId: string) =>
    setItems((prev) =>
      prev.map((f) =>
        f.fieldId === fieldId && f.uid !== ALWAYS_VISIBLE_UID
          ? { ...f, visible: !f.visible }
          : f,
      ),
    );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortableFields.findIndex((f) => f.fieldId === active.id);
    const newIndex = sortableFields.findIndex((f) => f.fieldId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortableFields, oldIndex, newIndex);
    const locked = items.filter((f) => f.uid === ALWAYS_VISIBLE_UID);
    setItems([...locked, ...reordered]);
  };

  return (
    <Slider.Dialog width="460" show={show} onClose={onClose} placement="start">
      <Slider.Header
        title="edit fields view"
        message="You can rearrange the fields to customize your view."
        className={styles.editFieldsHeader}
      />

      <Slider.Body className={styles.editFieldsBody}>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
          <SortableContext items={sortableFields.map((f) => f.fieldId)}>
            <div className={styles.editFieldsList}>
              {sortableFields.map((field) => (
                <SortableOption key={field.fieldId} field={field} onToggle={toggleVisibility} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </Slider.Body>

      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text="Confirm"
          onClick={() => onDone(items)}
        />
        <Button variant="secondary" text="Cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
};
